import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { ContentContainer, SwellEventCardContainer } from '@surfline/quiver-react';
import _get from 'lodash/get';
import { slugify, buoySubregionUrl } from '@surfline/web-common';
import { loadTreatment } from '@surfline/web-common/dist/features/redux';
import PageLoading from '../../../components/PageLoading';
import ForecastHeader from '../../../components/ForecastHeader';
import RegistrationModals from '../../../components/RegistrationModals';
import { fetchSwellEvents } from '../../../actions/swellEvents';
import { setRecentlyVisited } from '../../../actions/user';
import {
  clearOverviewData,
  fetchOverviewData,
  fetchNearbySubregions,
  fetchRegionalArticles,
} from '../../../actions/subregion';
import { navigatedToPage, trackEvent } from '../../../actions/segmentEvents';
import overviewDataPropShape from '../../../propTypes/subregionOverviewData';
import swellEventsPropType from '../../../propTypes/swellEvents';
import userDetailsPropType from '../../../propTypes/userDetails';
import userSettingsPropTypes from '../../../propTypes/userSettings';
import {
  getSubregionAssociated,
  getSubregionOverviewData,
  getNearbySubregions,
} from '../../../selectors/subregion';
import getSwellEvents from '../../../selectors/swellEvents';
import getTreatments from '../../../selectors/treatments';
import { getUserDetails, getPremiumStatus, getUserSettings } from '../../../selectors/user';
import en from '../../../intl/translations/en';
import config from '../../../config';
import withPageViews from '../../../common/withPageViews';
import nearbySubregionsPropTypes from '../../../propTypes/nearbySubregions';
import articlePropTypes from '../../../propTypes/article';
import treatmentsPropType from '../../../propTypes/treatments';
import devicePropTypes from '../../../propTypes/device';

class SubregionForecastPage extends Component {
  static fetch = [
    ({ subregionId }, { cookies }) => dispatch => dispatch(fetchOverviewData(subregionId, cookies)),
    ({ subregionId }, { cookies }) => dispatch =>
      dispatch(fetchNearbySubregions(subregionId, cookies)),
    ({ subregionId }, { cookies }) => dispatch =>
      dispatch(fetchRegionalArticles(subregionId, cookies)),
  ];

  componentDidMount() {
    const {
      match: {
        params: { subregionId },
      },
      userDetails,
      overviewData,
      doFetchOverviewData,
      doSetRecentlyVisited,
      doFetchRegionalArticles,
      doFetchSubregionSwellEvents,
      feed,
    } = this.props;
    if (!overviewData) {
      doFetchOverviewData(subregionId);
    } else if (userDetails) {
      doSetRecentlyVisited(subregionId, overviewData.name);
    }
    if (!feed && subregionId) {
      doFetchRegionalArticles(subregionId);
    }
    document.documentElement.style.overflow = 'auto';
    doFetchSubregionSwellEvents(subregionId);
  }

  componentDidUpdate(prevProps) {
    const {
      match: {
        params: { subregionId },
      },
      userDetails,
      overviewData,
      doFetchOverviewData,
      doSetRecentlyVisited,
    } = this.props;
    if (prevProps.match.params.subregionId !== subregionId) {
      doFetchOverviewData(subregionId);
    }

    if (overviewData && overviewData !== prevProps.overviewData) {
      this.navigatedToPage(overviewData);
      if (userDetails) {
        doSetRecentlyVisited(subregionId, overviewData.name);
      }
    }
  }

  componentWillUnmount() {
    const { doClearOverviewData } = this.props;
    doClearOverviewData();
    document.documentElement.style.overflow = null;
  }

  navigatedToPage = overviewData => {
    const { doNavigatedToPage } = this.props;
    return doNavigatedToPage('Regional Forecast', {
      category: 'regional forecast',
      areaName: overviewData.breadcrumb[overviewData.breadcrumb.length - 1].name,
      subregionId: overviewData._id,
      subregionName: overviewData.name,
    });
  };

  renderPageLevelLinks = (subregionName, chartsUrl) => {
    const {
      match: {
        params: { subregionId },
      },
      feed,
    } = this.props;
    const pageLinks = [
      {
        text: en.RegionalForecast.pageLevelLinkText.forecast.text,
        title: en.RegionalForecast.pageLevelLinkText.forecast.title(subregionName),
        path: en.RegionalForecast.pageLevelLinkText.forecast.path(
          slugify(subregionName),
          subregionId
        ),
        isIndex: true,
      },
    ];
    if (feed.articles.length > 0) {
      pageLinks.push({
        text: en.RegionalForecast.pageLevelLinkText.premiumAnalysis.text,
        title: en.RegionalForecast.pageLevelLinkText.premiumAnalysis.title(subregionName),
        path: en.RegionalForecast.pageLevelLinkText.premiumAnalysis.path(
          slugify(subregionName),
          subregionId
        ),
        isIndex: false,
      });
    }
    if (chartsUrl) {
      pageLinks.push({
        text: en.RegionalForecast.externalLinkText.charts,
        path: chartsUrl,
        newWindow: false,
      });
    }
    return pageLinks;
  };

  renderExternalLinks = (legacySubregionId, subregionAssociated = {}) => {
    const externalLinks = [
      {
        text: en.RegionalForecast.externalLinkText.buoys,
        path: buoySubregionUrl(legacySubregionId),
        newWindow: true,
      },
    ];

    if (subregionAssociated.localPhotosUrl) {
      externalLinks.push({
        text: en.RegionalForecast.externalLinkText.photos,
        path: subregionAssociated.localPhotosUrl,
        newWindow: true,
      });
    }
    if (subregionAssociated.beachesUrl) {
      externalLinks.push({
        text: en.RegionalForecast.externalLinkText.travel,
        path: subregionAssociated.beachesUrl,
        newWindow: false,
      });
    }

    return externalLinks;
  };

  onClickCard = swellEvent => {
    const {
      match: {
        params: { subregionId },
      },
      doTrackEvent,
      overviewData,
    } = this.props;
    doTrackEvent('Clicked Swell Alert Card', {
      title: swellEvent.name,
      contentType: 'Swell Alert',
      basins: swellEvent.basins.join(','),
      locationCategory: 'Region Forecast Page - Top',
      destinationUrl: swellEvent.permalink,
      subregionId,
      subregionName: overviewData.name,
    });
  };

  render() {
    const {
      subregionAssociated,
      overviewData,
      loading,
      userSettings,
      children,
      doTrackEvent,
      location,
      route,
      nearbySubregions,
      swellEvents,
      doLoadTreatments,
      treatments,
      pageViews,
      device,
      userDetails,
      isPremium,
    } = this.props;
    const externalLinks = this.renderExternalLinks(
      _get(subregionAssociated, 'advertising.subregionId', null)
    );
    const isMultiPage = route && route.isMultiPage;
    return (
      <div className="sl-subregion-forecast-page">
        {loading ? <PageLoading /> : null}
        {!loading && overviewData ? (
          <div>
            <RegistrationModals
              createAccountUrl={config.createAccountUrl}
              device={device}
              doLoadTreatments={doLoadTreatments}
              isPremium={isPremium}
              pageViews={pageViews}
              treatments={treatments}
              userDetails={userDetails}
            />
            <ContentContainer>
              {swellEvents.length ? (
                <SwellEventCardContainer
                  onClickCard={swellEvent => this.onClickCard(swellEvent)}
                  events={swellEvents}
                />
              ) : null}
              <ForecastHeader
                combinedSpotPage
                nearbySubregions={nearbySubregions}
                pathName={location.pathname}
                isMultiPage={isMultiPage}
                breadcrumb={overviewData.breadcrumb}
                pageTitle={`${overviewData.name} ${en.RegionalForecast.header.titleSuffix}`}
                nearbySpots={overviewData.spots}
                externalLinks1={externalLinks}
                pageLevelLinks={this.renderPageLevelLinks(
                  overviewData.name,
                  subregionAssociated ? subregionAssociated.chartsUrl : null,
                  subregionAssociated
                )}
                userSettings={userSettings}
                doTrackEvent={doTrackEvent}
              />
            </ContentContainer>
            {React.cloneElement(children)}
          </div>
        ) : null}
      </div>
    );
  }
}

SubregionForecastPage.propTypes = {
  children: PropTypes.node,
  nearbySubregions: nearbySubregionsPropTypes,
  doNavigatedToPage: PropTypes.func.isRequired,
  doClearOverviewData: PropTypes.func.isRequired,
  doFetchOverviewData: PropTypes.func.isRequired,
  doFetchRegionalArticles: PropTypes.func.isRequired,
  doFetchSubregionSwellEvents: PropTypes.func.isRequired,
  doLoadTreatments: PropTypes.func.isRequired,
  doSetRecentlyVisited: PropTypes.func.isRequired,
  doTrackEvent: PropTypes.func.isRequired,
  feed: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.bool,
    articles: PropTypes.arrayOf(articlePropTypes),
  }).isRequired,
  subregionAssociated: PropTypes.shape({
    chartsUrl: PropTypes.string,
  }),
  overviewData: overviewDataPropShape,
  loading: PropTypes.bool,
  match: PropTypes.shape({
    params: PropTypes.shape({
      subregionId: PropTypes.string,
      subregionName: PropTypes.string,
    }).isRequired,
  }).isRequired,
  swellEvents: swellEventsPropType,
  userDetails: userDetailsPropType,
  userSettings: userSettingsPropTypes,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  route: PropTypes.shape({
    isMultiPage: PropTypes.bool,
  }),
  device: devicePropTypes.isRequired,
  pageViews: PropTypes.number.isRequired,
  treatments: treatmentsPropType,
  isPremium: PropTypes.bool.isRequired,
};

SubregionForecastPage.defaultProps = {
  nearbySubregions: null,
  subregionAssociated: {
    chartsUrl: null,
  },
  overviewData: null,
  loading: false,
  userDetails: null,
  userSettings: null,
  children: null,
  location: {},
  route: {
    isMultiPage: false,
  },
  swellEvents: [],
  treatments: {},
};

export default withPageViews(
  connect(
    state => ({
      loading: state.subregion.overview.loading,
      subregionAssociated: getSubregionAssociated(state),
      userDetails: getUserDetails(state),
      overviewData: getSubregionOverviewData(state),
      isPremium: getPremiumStatus(state),
      userSettings: getUserSettings(state),
      nearbySubregions: getNearbySubregions(state),
      feed: state.subregion.feed,
      swellEvents: getSwellEvents(state),
      treatments: getTreatments(state),
    }),
    dispatch => ({
      doFetchRegionalArticles: subregionId => dispatch(fetchRegionalArticles(subregionId)),
      doNavigatedToPage: (event, properties) => dispatch(navigatedToPage(event, properties)),
      doClearOverviewData: () => dispatch(clearOverviewData()),
      doFetchOverviewData: subregionId => dispatch(fetchOverviewData(subregionId)),
      doFetchSubregionSwellEvents: subregionId =>
        dispatch(fetchSwellEvents(subregionId, 'subregion')),
      doFetchNearbySubregions: subregionId => dispatch(fetchNearbySubregions(subregionId)),
      doSetRecentlyVisited: (subregionId, name) =>
        dispatch(setRecentlyVisited('subregions', subregionId, name)),
      doTrackEvent: (event, properties) => dispatch(trackEvent(event, properties)),
      doLoadTreatments: treatment => dispatch(loadTreatment(treatment)),
    })
  )(withRouter(SubregionForecastPage)),
  'subregionForecastPage',
  'match.params.subregionId'
);
